import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

// Fallback languages if Redux state is not available
const FALLBACK_LANGUAGES = [
  {
    code: "en",
    name: "English",
    native_name: "English",
    direction: "ltr",
    is_active: true,
  },
  {
    code: "hi",
    name: "Hindi",
    native_name: "हिन्दी",
    direction: "ltr",
    is_active: true,
  },
  {
    code: "ur",
    name: "Urdu",
    native_name: "اردو",
    direction: "rtl",
    is_active: true,
  },
  {
    code: "hinglish",
    name: "Hinglish",
    native_name: "Hinglish",
    direction: "ltr",
    is_active: true,
  },
  {
    code: "ar",
    name: "Arabic",
    native_name: "العربية",
    direction: "rtl",
    is_active: true,
  },
  {
    code: "fr",
    name: "French",
    native_name: "Français",
    direction: "ltr",
    is_active: true,
  },
  {
    code: "es",
    name: "Spanish",
    native_name: "Español",
    direction: "ltr",
    is_active: true,
  },
];

/** Empty multilingual object for form defaults (all active language codes). */
export function emptyMultilingual(codes = FALLBACK_LANGUAGES.map((l) => l.code)) {
  return Object.fromEntries(codes.map((c) => [c, ""]));
}

/**
 * MultilingualInput Component
 * Handles JSONB multilingual content with language tabs
 *
 * @param {Object} value - Object with language codes as keys: { en: "...", ar: "...", ur: "..." }
 * @param {Function} onChange - Called with updated multilingual object
 * @param {string} label - Field label
 * @param {string} placeholder - Placeholder text
 * @param {boolean} multiline - Use textarea instead of input
 * @param {number} rows - Number of rows for textarea
 * @param {boolean} required - Mark field as required
 * @param {string} defaultLanguage - Default language to show (default: 'en')
 */
export function MultilingualInput({
  value = {},
  onChange,
  label,
  placeholder = "",
  multiline = false,
  rows = 3,
  required = false,
  defaultLanguage = "en",
  className,
  ...props
}) {
  // Get active languages from Redux or use fallback
  const { languages: systemLanguages = [] } = useSelector(
    (state) => state.language || {}
  );
  const activeLanguages =
    systemLanguages.filter((lang) => lang.is_active).length > 0
      ? systemLanguages
          .filter((lang) => lang.is_active)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      : FALLBACK_LANGUAGES;

  const [activeLanguage, setActiveLanguage] = useState(defaultLanguage);

  const handleLanguageChange = (langCode, text) => {
    const updated = {
      ...value,
      [langCode]: text,
    };
    onChange(updated);
  };

  const hasContent = (langCode) => {
    return value[langCode] && value[langCode].trim().length > 0;
  };

  const getFilledCount = () => {
    return activeLanguages.filter((lang) => hasContent(lang.code)).length;
  };

  const InputComponent = multiline ? Textarea : Input;

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        {label && (
          <Label className="text-sm font-medium">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs font-normal">
            {getFilledCount()} / {activeLanguages.length} filled
          </Badge>
        </div>
      </div>

      <Tabs
        value={activeLanguage}
        onValueChange={setActiveLanguage}
        className="w-full"
      >
        <TabsList className="w-full justify-start h-auto p-1 bg-muted/50 rounded-lg">
          {activeLanguages.map((lang) => (
            <TabsTrigger
              key={lang.code}
              value={lang.code}
              className={cn(
                "relative px-4 py-2.5 rounded-md transition-all",
                "data-[state=active]:bg-background data-[state=active]:shadow-sm",
                "hover:bg-background/50"
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  {lang.native_name || lang.name}
                </span>
                {lang.direction === "rtl" && (
                  <span className="text-[10px] text-muted-foreground">RTL</span>
                )}
                {hasContent(lang.code) && (
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                )}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {activeLanguages.map((lang) => (
          <TabsContent
            key={lang.code}
            value={lang.code}
            className="mt-4 space-y-2"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">
                  {lang.name} ({lang.code.toUpperCase()})
                </Label>
                {lang.direction === "rtl" && (
                  <Badge variant="outline" className="h-5 text-[10px] px-1.5">
                    Right-to-Left
                  </Badge>
                )}
              </div>
              {hasContent(lang.code) && (
                <Badge className="h-5 px-2 text-[10px] bg-green-500/10 text-green-700 border-green-500/20">
                  ✓ Completed
                </Badge>
              )}
            </div>

            <InputComponent
              value={value[lang.code] || ""}
              onChange={(e) => handleLanguageChange(lang.code, e.target.value)}
              placeholder={`${placeholder}`}
              className={cn("w-full", lang.direction === "rtl" && "text-right")}
              dir={lang.direction || "ltr"}
              rows={multiline ? rows : undefined}
              {...props}
            />

            {required && lang.code === "en" && !hasContent("en") && (
              <p className="text-xs text-destructive">
                English translation is required
              </p>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {required && (
        <p className="text-xs text-muted-foreground">
          * At least English translation is required
        </p>
      )}
    </div>
  );
}

/**
 * MultilingualEditor Component
 * For rich text or complex multilingual content with custom editors
 */
export function MultilingualEditor({
  value = {},
  onChange,
  label,
  required = false,
  defaultLanguage = "en",
  renderEditor,
  className,
}) {
  // Get active languages from Redux or use fallback
  const { languages: systemLanguages = [] } = useSelector(
    (state) => state.language || {}
  );
  const activeLanguages =
    systemLanguages.filter((lang) => lang.is_active).length > 0
      ? systemLanguages
          .filter((lang) => lang.is_active)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      : FALLBACK_LANGUAGES;

  const [activeLanguage, setActiveLanguage] = useState(defaultLanguage);

  const handleLanguageChange = (langCode, content) => {
    const updated = {
      ...value,
      [langCode]: content,
    };
    onChange(updated);
  };

  const hasContent = (langCode) => {
    return value[langCode] && value[langCode].trim().length > 0;
  };

  const getFilledCount = () => {
    return activeLanguages.filter((lang) => hasContent(lang.code)).length;
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        {label && (
          <Label className="text-sm font-medium">
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs font-normal">
            {getFilledCount()} / {activeLanguages.length} filled
          </Badge>
        </div>
      </div>

      <Tabs
        value={activeLanguage}
        onValueChange={setActiveLanguage}
        className="w-full"
      >
        <TabsList className="w-full justify-start h-auto p-1 bg-muted/50 rounded-lg">
          {activeLanguages.map((lang) => (
            <TabsTrigger
              key={lang.code}
              value={lang.code}
              className={cn(
                "relative px-4 py-2.5 rounded-md transition-all",
                "data-[state=active]:bg-background data-[state=active]:shadow-sm",
                "hover:bg-background/50"
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  {lang.native_name || lang.name}
                </span>
                {lang.direction === "rtl" && (
                  <span className="text-[10px] text-muted-foreground">RTL</span>
                )}
                {hasContent(lang.code) && (
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                )}
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        {activeLanguages.map((lang) => (
          <TabsContent
            key={lang.code}
            value={lang.code}
            className="mt-4 space-y-2"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">
                  {lang.name} ({lang.code.toUpperCase()})
                </Label>
                {lang.direction === "rtl" && (
                  <Badge variant="outline" className="h-5 text-[10px] px-1.5">
                    Right-to-Left
                  </Badge>
                )}
              </div>
              {hasContent(lang.code) && (
                <Badge className="h-5 px-2 text-[10px] bg-green-500/10 text-green-700 border-green-500/20">
                  ✓ Completed
                </Badge>
              )}
            </div>

            {renderEditor({
              value: value[lang.code] || "",
              onChange: (content) => handleLanguageChange(lang.code, content),
              language: lang,
            })}
          </TabsContent>
        ))}
      </Tabs>

      {required && (
        <p className="text-xs text-muted-foreground">
          * At least English translation is required
        </p>
      )}
    </div>
  );
}

export { FALLBACK_LANGUAGES as LANGUAGES };
